import ContractFormCst from "../ContractFormCst";
import dayjs from "dayjs";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { fetcher } from "src/services/config";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box, Container } from "@mui/material";
import { ServicesURL, getAllErrorsAsString, uploadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useState, useEffect } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import ButtonBackPage from "@components/ButtonBackPage";
import { useAddEditContract } from "../hooks/useAddEditContract";

const EditContract: React.FC = () => {
  const { t } = useTranslation(["contracts-page"]);
  const { idContract, id } = useParams();
  const navigate = useNavigate();

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getContracts}`,
    idContract,
    PagesID["contracts"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetContractAssociations,
    accountsAssociation,
    supplierAssociation,
    openAssociations,
  } = useAddEditContract(dataDetail?.dto || []);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerContract } = useSWRMutation(
    `${ServicesURL.postContracts}/${idContract}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.contracts.messageLoading"),
        title: t("edit.outcome.contracts.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.contracts.messageLoading"),
      title: t("edit.outcome.contracts.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    const objToSend: any = {
      sap_code: inputForm.sap_code || null,
      start_date: !!inputForm.start_date
        ? dayjs(new Date(inputForm.start_date)).format("YYYY-MM-DD")
        : null,
      end_date: !!inputForm.end_date
        ? dayjs(new Date(inputForm.end_date)).format("YYYY-MM-DD")
        : null,
      nominal_value: Number(inputForm.nominal_value) || null,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      account_id:
        accountsAssociation && accountsAssociation.length > 0
          ? parseInt(accountsAssociation[0].id)
          : null,
      description: inputForm.description || null,
    };

    try {
      await triggerContract(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.contracts.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-contracts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <ContractFormCst
              row={dataDetail?.dto || []}
              columns={columns}
              onSubmit={onSubmit}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              resetContractAssociations={resetContractAssociations}
              getValues={(accessorKey) => getValues(accessorKey)}
              association={openAssociations}
              backButton={() => {
                navigate(-1);
              }}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              id
                ? `/corporate/${id}/detail/budget-management/setup/contracts`
                : `/budget-management/setup/contracts`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditContract;
